// Inventory Engine Phase 2 — Suppliers, Goods Inward (GRN) and Lots & Expiry.
//
// Same rules as the Phase 1 page: every quantity is a string from the
// server and the page does no quantity arithmetic (the base-unit preview
// of a draft line is computed by the server); posting and voiding carry an
// idempotency key minted once per attempt. FEFO/FIFO here is an ordering
// answer only — nothing in Phase 2 deducts stock by it.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Field, Select, fail } from "./fields";
import {
  compactPayload, levelLabel, lineSummary, lotDateLabel, newOperationKey, receiptStatusVariant, todayISO,
} from "../../lib/inventory";

const itemOptions = (items) => items.filter((i) => i.status === "active").map((i) => ({ value: i.id, label: `${i.item_code} — ${i.name}` }));
const levelOptions = (item) => (item?.packaging_levels || []).filter((l) => l.status === "active").map((l) => ({ value: l.level_id, label: levelLabel(l) }));

// ================================================================ Suppliers

const blankSupplier = { code: "", name: "", contact_name: "", phone: "", email: "", line1: "", city: "", state: "", postal_code: "", notes: "" };

function supplierBody(f, { includeCode }) {
  const address = compactPayload({ line1: f.line1, city: f.city, state: f.state, postal_code: f.postal_code });
  return {
    ...(includeCode ? { code: f.code } : {}),
    ...compactPayload({ name: f.name, contact_name: f.contact_name, phone: f.phone, email: f.email, notes: f.notes }),
    address: Object.keys(address).length ? address : null,
  };
}

function SupplierForm({ value, onChange, includeCode }) {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      {includeCode && <Field label="Code"><Input value={value.code} onChange={(e) => set({ code: e.target.value.toUpperCase() })} data-testid="supplier-code" /></Field>}
      <Field label="Name"><Input value={value.name} onChange={(e) => set({ name: e.target.value })} data-testid="supplier-name" /></Field>
      <Field label="Contact"><Input value={value.contact_name} onChange={(e) => set({ contact_name: e.target.value })} /></Field>
      <Field label="Phone"><Input value={value.phone} onChange={(e) => set({ phone: e.target.value })} /></Field>
      <Field label="Email"><Input value={value.email} onChange={(e) => set({ email: e.target.value })} /></Field>
      <Field label="Address"><Input value={value.line1} onChange={(e) => set({ line1: e.target.value })} /></Field>
      <Field label="City"><Input value={value.city} onChange={(e) => set({ city: e.target.value })} /></Field>
      <Field label="State"><Input value={value.state} onChange={(e) => set({ state: e.target.value })} /></Field>
      <Field label="ZIP"><Input value={value.postal_code} onChange={(e) => set({ postal_code: e.target.value })} /></Field>
      <Field label="Notes"><Input value={value.notes} onChange={(e) => set({ notes: e.target.value })} /></Field>
    </div>
  );
}

function SupplierDetail({ supplierId, items, onChanged }) {
  const [supplier, setSupplier] = useState(null);
  const [edit, setEdit] = useState(blankSupplier);
  const [si, setSi] = useState({ item_id: "", supplier_product_code: "", supplier_sku: "", supplier_barcode: "", purchase_packaging_level_id: "", lead_time_days: "", moq: "", preferred: false });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/inventory/suppliers/${supplierId}`);
      setSupplier(data);
      const a = data.address || {};
      setEdit({ ...blankSupplier, ...compactPayload({ name: data.name, contact_name: data.contact_name, phone: data.phone, email: data.email, notes: data.notes }),
        line1: a.line1 || "", city: a.city || "", state: a.state || "", postal_code: a.postal_code || "" });
    } catch (e) { fail(e, "Could not load supplier"); }
  }, [supplierId]);
  useEffect(() => { load(); }, [load]);

  if (!supplier) return <Card className="p-4 text-sm text-muted-foreground">Loading supplier…</Card>;
  const call = async (fn, ok) => {
    try { await fn(); toast.success(ok); await load(); onChanged(); } catch (e) { fail(e, "Request failed"); }
  };
  const siItem = items.find((i) => i.id === si.item_id);
  const itemCode = (id) => items.find((i) => i.id === id)?.item_code || id;

  return (
    <Card className="p-4 space-y-4" data-testid="supplier-detail">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">{supplier.code} — {supplier.name}</h3>
          <p className="text-xs text-muted-foreground">{supplier.active ? "Active" : "Inactive"} · code cannot change once created</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          Active
          <Switch checked={Boolean(supplier.active)} data-testid="supplier-active-switch"
            onCheckedChange={(v) => call(() => api.patch(`/inventory/suppliers/${supplier.id}`, { active: v }), v ? "Supplier activated" : "Supplier deactivated")} />
        </div>
      </div>
      <SupplierForm value={edit} onChange={setEdit} includeCode={false} />
      <Button size="sm" onClick={() => call(() => api.patch(`/inventory/suppliers/${supplier.id}`, supplierBody(edit, { includeCode: false })), "Supplier saved")}>Save supplier</Button>

      <div>
        <div className="text-sm font-medium mb-1">Supplier items</div>
        <Table>
          <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Supplier code</TableHead><TableHead>SKU / barcode</TableHead><TableHead>Lead time</TableHead><TableHead>MOQ</TableHead><TableHead>Last cost</TableHead><TableHead>State</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {(supplier.supplier_items || []).map((r) => (
              <TableRow key={r.id} data-testid={`supplier-item-${r.supplier_product_code}`}>
                <TableCell className="font-mono">{itemCode(r.item_id)}</TableCell>
                <TableCell className="font-mono">{r.supplier_product_code}</TableCell>
                <TableCell>{[r.supplier_sku, r.supplier_barcode].filter(Boolean).join(" / ") || "—"}</TableCell>
                <TableCell>{r.lead_time_days != null ? `${r.lead_time_days} d` : "—"}</TableCell>
                <TableCell>{r.moq || "—"}</TableCell>
                <TableCell>{r.last_cost ? `${r.last_cost.amount_per_uom} ${r.last_cost.currency}/${r.last_cost.uom_code}` : "—"}</TableCell>
                <TableCell className="space-x-1">
                  <Badge variant={r.active ? "secondary" : "outline"}>{r.active ? "active" : "inactive"}</Badge>
                  {r.preferred && <Badge>preferred</Badge>}
                </TableCell>
                <TableCell className="space-x-1 whitespace-nowrap">
                  {r.active && !r.preferred && <Button size="sm" variant="ghost" onClick={() => call(() => api.patch(`/inventory/supplier-items/${r.id}`, { preferred: true }), "Marked preferred")}>Prefer</Button>}
                  <Button size="sm" variant="ghost" onClick={() => call(() => api.patch(`/inventory/supplier-items/${r.id}`, { active: !r.active }), r.active ? "Deactivated" : "Activated")}>{r.active ? "Deactivate" : "Activate"}</Button>
                </TableCell>
              </TableRow>
            ))}
            {(supplier.supplier_items || []).length === 0 && <TableRow><TableCell colSpan={8} className="text-sm text-muted-foreground">No supplier items yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end mt-2">
          <Field label="Inventory item"><Select value={si.item_id} onChange={(v) => setSi({ ...si, item_id: v, purchase_packaging_level_id: "" })} options={itemOptions(items)} placeholder="Choose…" testId="si-item" /></Field>
          <Field label="Supplier product code"><Input value={si.supplier_product_code} onChange={(e) => setSi({ ...si, supplier_product_code: e.target.value })} data-testid="si-code" /></Field>
          <Field label="Supplier SKU"><Input value={si.supplier_sku} onChange={(e) => setSi({ ...si, supplier_sku: e.target.value })} /></Field>
          <Field label="Supplier barcode"><Input value={si.supplier_barcode} onChange={(e) => setSi({ ...si, supplier_barcode: e.target.value })} /></Field>
          <Field label="Purchase unit"><Select value={si.purchase_packaging_level_id} onChange={(v) => setSi({ ...si, purchase_packaging_level_id: v })} options={levelOptions(siItem)} placeholder="Not set" /></Field>
          <Field label="Lead time (days)"><Input value={si.lead_time_days} inputMode="numeric" onChange={(e) => setSi({ ...si, lead_time_days: e.target.value })} /></Field>
          <Field label="MOQ (purchase units)"><Input value={si.moq} inputMode="decimal" onChange={(e) => setSi({ ...si, moq: e.target.value })} /></Field>
          <label className="flex items-center gap-2 text-sm h-9"><Switch checked={si.preferred} onCheckedChange={(v) => setSi({ ...si, preferred: v })} />Preferred supplier for this item</label>
        </div>
        <Button size="sm" className="mt-2" disabled={!si.item_id || !si.supplier_product_code} data-testid="si-add"
          onClick={() => call(async () => {
            await api.post("/inventory/supplier-items", {
              supplier_id: supplier.id,
              ...compactPayload({ ...si, preferred: undefined, lead_time_days: undefined }),
              preferred: si.preferred,
              lead_time_days: si.lead_time_days === "" ? undefined : parseInt(si.lead_time_days, 10),
            });
            setSi({ ...si, supplier_product_code: "", supplier_sku: "", supplier_barcode: "" });
          }, "Supplier item added")}>Add supplier item</Button>
      </div>
    </Card>
  );
}

export function SuppliersPanel({ items, onChanged }) {
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState(blankSupplier);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setSuppliers((await api.get("/inventory/suppliers")).data.suppliers || []); } catch (e) { fail(e, "Could not load suppliers"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/suppliers", supplierBody(form, { includeCode: true }));
      toast.success(`Created supplier ${data.code}`);
      setForm(blankSupplier);
      await load();
      setSelected(data.id);
    } catch (e) { fail(e, "Could not create supplier"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="inventory-suppliers-panel">
      <Card className="p-4 space-y-3">
        <h3 className="font-semibold">New supplier</h3>
        <SupplierForm value={form} onChange={setForm} includeCode />
        <Button onClick={create} disabled={busy || !form.code || !form.name} data-testid="supplier-create">Create supplier</Button>
      </Card>
      <Card className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {suppliers.map((s) => (
              <TableRow key={s.id} className={`cursor-pointer ${selected === s.id ? "bg-muted" : ""}`} onClick={() => setSelected(s.id)} data-testid={`supplier-row-${s.code}`}>
                <TableCell className="font-mono">{s.code}</TableCell>
                <TableCell>{s.name}</TableCell>
                <TableCell>{[s.contact_name, s.phone, s.email].filter(Boolean).join(" · ") || "—"}</TableCell>
                <TableCell><Badge variant={s.active ? "secondary" : "outline"}>{s.active ? "active" : "inactive"}</Badge></TableCell>
              </TableRow>
            ))}
            {suppliers.length === 0 && <TableRow><TableCell colSpan={4} className="text-sm text-muted-foreground">No suppliers yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
      {selected && <SupplierDetail key={selected} supplierId={selected} items={items} onChanged={() => { load(); onChanged(); }} />}
    </div>
  );
}

// ================================================================ Goods inward

const blankLine = {
  item_id: "", supplier_item_id: "", packaging_level_id: "", document_qty: "", received_qty: "",
  accepted_qty: "", quarantine_qty: "", damaged_qty: "", rejected_qty: "", supplier_lot: "",
  mfg_date: "", expiry_date: "", best_before_date: "", location_id: "", unit_cost: "", discrepancy_reason: "", notes: "",
};

function LineForm({ receipt, items, locations, vocab, onSaved }) {
  const [line, setLine] = useState(blankLine);
  const [supplierItems, setSupplierItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const item = items.find((i) => i.id === line.item_id);
  const set = (patch) => setLine({ ...line, ...patch });

  useEffect(() => {
    api.get("/inventory/supplier-items", { params: { supplier_id: receipt.supplier_id, include_inactive: false } })
      .then((r) => setSupplierItems(r.data.supplier_items || [])).catch(() => setSupplierItems([]));
  }, [receipt.supplier_id]);

  const add = async () => {
    setBusy(true);
    try {
      await api.post(`/inventory/receipts/${receipt.id}/lines`, compactPayload(line));
      toast.success("Line added");
      setLine(blankLine);
      onSaved();
    } catch (e) { fail(e, "Line refused"); } finally { setBusy(false); }
  };

  const siOptions = supplierItems.filter((s) => !line.item_id || s.item_id === line.item_id)
    .map((s) => ({ value: s.id, label: `${s.supplier_product_code}${s.preferred ? " (preferred)" : ""}` }));
  const stock = locations.filter((l) => l.active && l.stock_holding && l.site_id === receipt.site_id);
  const policy = item ? `${item.expiry_tracking || "none"} dates · supplier lot ${item.lot_capture || "optional"}${item.quarantine_on_receipt ? " · inspect on receipt" : ""}` : "";

  return (
    <div className="space-y-2 border rounded-md p-3" data-testid="grn-line-form">
      <div className="text-sm font-medium">Add line {policy && <span className="text-xs text-muted-foreground">— {policy}</span>}</div>
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
        <Field label="Item"><Select value={line.item_id} onChange={(v) => set({ item_id: v, packaging_level_id: "", supplier_item_id: "" })} options={itemOptions(items)} placeholder="Choose…" testId="grn-line-item" /></Field>
        <Field label="Supplier item"><Select value={line.supplier_item_id} onChange={(v) => set({ supplier_item_id: v })} options={siOptions} placeholder="None" /></Field>
        <Field label="Receiving unit"><Select value={line.packaging_level_id} onChange={(v) => set({ packaging_level_id: v })} options={levelOptions(item)} placeholder="Supplier's / base unit" testId="grn-line-unit" /></Field>
        <Field label="Document qty"><Input value={line.document_qty} inputMode="decimal" onChange={(e) => set({ document_qty: e.target.value })} data-testid="grn-line-doc-qty" /></Field>
        <Field label="Received qty"><Input value={line.received_qty} inputMode="decimal" onChange={(e) => set({ received_qty: e.target.value })} data-testid="grn-line-received-qty" /></Field>
        <Field label="Accepted (blank = all)"><Input value={line.accepted_qty} inputMode="decimal" onChange={(e) => set({ accepted_qty: e.target.value })} data-testid="grn-line-accepted-qty" /></Field>
        <Field label="Quarantine"><Input value={line.quarantine_qty} inputMode="decimal" onChange={(e) => set({ quarantine_qty: e.target.value })} /></Field>
        <Field label="Damaged (kept)"><Input value={line.damaged_qty} inputMode="decimal" onChange={(e) => set({ damaged_qty: e.target.value })} /></Field>
        <Field label="Rejected"><Input value={line.rejected_qty} inputMode="decimal" onChange={(e) => set({ rejected_qty: e.target.value })} /></Field>
        <Field label="Supplier lot"><Input value={line.supplier_lot} onChange={(e) => set({ supplier_lot: e.target.value })} data-testid="grn-line-supplier-lot" /></Field>
        <Field label="Mfg date"><Input type="date" value={line.mfg_date} onChange={(e) => set({ mfg_date: e.target.value })} /></Field>
        <Field label="Expiry date"><Input type="date" value={line.expiry_date} onChange={(e) => set({ expiry_date: e.target.value })} data-testid="grn-line-expiry" /></Field>
        <Field label="Best before"><Input type="date" value={line.best_before_date} onChange={(e) => set({ best_before_date: e.target.value })} /></Field>
        <Field label="Location"><Select value={line.location_id} onChange={(v) => set({ location_id: v })} options={stock.map((l) => ({ value: l.id, label: l.path }))} placeholder={receipt.location_path} /></Field>
        <Field label={`Unit cost (${receipt.currency} per receiving unit)`}><Input value={line.unit_cost} inputMode="decimal" onChange={(e) => set({ unit_cost: e.target.value })} /></Field>
        <Field label="Discrepancy reason"><Select value={line.discrepancy_reason} onChange={(v) => set({ discrepancy_reason: v })} options={(vocab?.discrepancy_reasons || []).map((r) => ({ value: r, label: r.replace(/_/g, " ") }))} placeholder="None" /></Field>
        <Field label="Notes"><Input value={line.notes} onChange={(e) => set({ notes: e.target.value })} /></Field>
      </div>
      <Button size="sm" onClick={add} disabled={busy || (!line.item_id && !line.supplier_item_id) || !line.received_qty} data-testid="grn-line-add">Add line</Button>
    </div>
  );
}

function ReceiptDetail({ receiptId, items, locations, vocab, onChanged }) {
  const [receipt, setReceipt] = useState(null);
  const [postKey, setPostKey] = useState(newOperationKey);
  const [voidKey, setVoidKey] = useState(newOperationKey);
  const [voidReason, setVoidReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setReceipt((await api.get(`/inventory/receipts/${receiptId}`)).data); } catch (e) { fail(e, "Could not load receipt"); }
  }, [receiptId]);
  useEffect(() => { load(); }, [load]);

  if (!receipt) return <Card className="p-4 text-sm text-muted-foreground">Loading receipt…</Card>;
  const draft = receipt.status === "DRAFT";
  const run = async (fn, ok, after) => {
    setBusy(true);
    try { const res = await fn(); toast.success(typeof ok === "function" ? ok(res) : ok); if (after) after(); await load(); onChanged(); } catch (e) { fail(e, "Request refused"); } finally { setBusy(false); }
  };

  return (
    <Card className="p-4 space-y-4" data-testid="grn-detail">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold">{receipt.receipt_no} <Badge variant={receiptStatusVariant(receipt.status)} data-testid="grn-status">{receipt.status}</Badge></h3>
          <p className="text-xs text-muted-foreground">
            {receipt.supplier_code} — {receipt.supplier_name} · slip {receipt.delivery_slip_no || "—"} · invoice {receipt.invoice_no || "—"} {receipt.invoice_date ? `(${receipt.invoice_date})` : ""} · into {receipt.location_path} · received {receipt.received_at?.slice(0, 16).replace("T", " ")} UTC by {receipt.received_by?.email}
          </p>
          {receipt.operation_no && <p className="text-xs text-muted-foreground">Ledger operation {receipt.operation_no} · posted {receipt.posted_at?.slice(0, 16).replace("T", " ")} UTC</p>}
          {receipt.status === "VOIDED" && <p className="text-xs text-destructive">Voided: {receipt.void_reason}</p>}
        </div>
        {draft && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={busy} onClick={() => run(() => api.post(`/inventory/receipts/${receipt.id}/cancel`, {}), "Draft cancelled")}>Cancel draft</Button>
            <Button size="sm" disabled={busy || receipt.lines.length === 0} data-testid="grn-post"
              onClick={() => run(() => api.post(`/inventory/receipts/${receipt.id}/post`, { idempotency_key: postKey }),
                (res) => (res.data.outcome === "already_applied" ? "Already posted — no change made" : `Posted ${res.data.receipt.receipt_no}`),
                () => setPostKey(newOperationKey()))}>
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Post receipt
            </Button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>#</TableHead><TableHead>Item</TableHead><TableHead>Doc / received</TableHead><TableHead>Accepted · Quar. · Dmg · Rej.</TableHead>
            <TableHead>Base equivalent</TableHead><TableHead>Short / excess</TableHead><TableHead>Lot</TableHead><TableHead>Dates</TableHead><TableHead>Cost</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {receipt.lines.map((l) => {
              const d = draft ? l.preview : { ...l, base_uom: l.base_uom };
              return (
                <TableRow key={l.line_id} data-testid={`grn-line-${l.line_no}`}>
                  <TableCell>{l.line_no}</TableCell>
                  <TableCell><span className="font-mono">{l.item_code}</span>{l.supplier_product_code && <div className="text-xs text-muted-foreground">{l.supplier_product_code}</div>}</TableCell>
                  <TableCell>{l.document_qty} / {l.received_qty} {l.uom_code}</TableCell>
                  <TableCell>{l.accepted_qty} · {l.quarantine_qty} · {l.damaged_qty} · {l.rejected_qty}</TableCell>
                  <TableCell data-testid={`grn-line-${l.line_no}-base`}>
                    {d?.error ? <span className="text-destructive text-xs">{d.message || d.error}</span> : d?.base ? (
                      <span>{l.received_qty} {l.uom_code} × {d.factor_to_base} = {d.base.received} {l.base_uom}<div className="text-xs text-muted-foreground">{lineSummary(l)}</div></span>
                    ) : "—"}
                  </TableCell>
                  <TableCell>{d?.short_qty ?? l.short_qty ?? "—"} / {d?.excess_qty ?? l.excess_qty ?? "—"}{l.discrepancy_reason && <div className="text-xs text-muted-foreground">{l.discrepancy_reason.replace(/_/g, " ")}</div>}</TableCell>
                  <TableCell>{l.lot_no ? <span className="font-mono">{l.lot_no}</span> : (draft ? "on post" : "—")}{l.supplier_lot && <div className="text-xs text-muted-foreground">supplier {l.supplier_lot}</div>}</TableCell>
                  <TableCell className="text-xs">{lotDateLabel(l)}{l.mfg_date && <div>mfg {l.mfg_date}</div>}</TableCell>
                  <TableCell className="text-xs">{l.unit_cost ? `${l.unit_cost}/${l.uom_code}` : "—"}{l.unit_cost_base && <div>{l.unit_cost_base}/{l.base_uom}</div>}</TableCell>
                  <TableCell>{draft && <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => api.delete(`/inventory/receipts/${receipt.id}/lines/${l.line_id}`), "Line removed")}>Remove</Button>}</TableCell>
                </TableRow>
              );
            })}
            {receipt.lines.length === 0 && <TableRow><TableCell colSpan={10} className="text-sm text-muted-foreground">No lines yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {draft && <LineForm receipt={receipt} items={items} locations={locations} vocab={vocab} onSaved={load} />}

      {receipt.status === "POSTED" && (
        <div className="flex flex-wrap items-end gap-2 border-t pt-3">
          <Field label="Void reason (reverses this receipt's stock; refused if any of it has moved since)">
            <Input value={voidReason} onChange={(e) => { setVoidReason(e.target.value); setVoidKey(newOperationKey()); }} className="w-96 max-w-full" data-testid="grn-void-reason" />
          </Field>
          <Button variant="destructive" size="sm" disabled={busy || voidReason.trim().length < 5} data-testid="grn-void"
            onClick={() => run(() => api.post(`/inventory/receipts/${receipt.id}/void`, { idempotency_key: voidKey, reason: voidReason }),
              (res) => (res.data.outcome === "already_applied" ? "Already voided" : "Receipt voided"), () => setVoidKey(newOperationKey()))}>Void receipt</Button>
        </div>
      )}
    </Card>
  );
}

export function GoodsInwardPanel({ items, locations, vocab, settings, onChanged }) {
  const [receipts, setReceipts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ supplier_id: "", location_id: "", delivery_slip_no: "", invoice_no: "", invoice_date: todayISO(), currency: "USD", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([api.get("/inventory/receipts", { params: compactPayload({ status }) }),
        api.get("/inventory/suppliers", { params: { active: true } })]);
      setReceipts(r.data.receipts || []);
      setSuppliers(s.data.suppliers || []);
    } catch (e) { fail(e, "Could not load receipts"); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const stock = locations.filter((l) => l.active && l.stock_holding);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/receipts", compactPayload(form));
      toast.success(`Draft ${data.receipt_no} created`);
      await load();
      setSelected(data.id);
    } catch (e) { fail(e, "Could not create receipt"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="inventory-goods-inward-panel">
      <Card className="p-4 space-y-3">
        <h3 className="font-semibold">New goods inward (GRN)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Field label="Supplier"><Select value={form.supplier_id} onChange={(v) => setForm({ ...form, supplier_id: v })} options={suppliers.map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` }))} placeholder="Choose…" testId="grn-supplier" /></Field>
          <Field label="Receive into"><Select value={form.location_id} onChange={(v) => setForm({ ...form, location_id: v })} options={stock.map((l) => ({ value: l.id, label: l.path }))} placeholder="Default stock location" /></Field>
          <Field label="Delivery slip no."><Input value={form.delivery_slip_no} onChange={(e) => setForm({ ...form, delivery_slip_no: e.target.value })} data-testid="grn-slip" /></Field>
          <Field label="Invoice no."><Input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} data-testid="grn-invoice" /></Field>
          <Field label="Invoice date"><Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} /></Field>
          <Field label="Currency"><Input value={form.currency} maxLength={3} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} /></Field>
          <Field label="Notes"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        </div>
        <Button onClick={create} disabled={busy || !form.supplier_id} data-testid="grn-create">Create draft</Button>
        <p className="text-xs text-muted-foreground">Numbers: {settings.document_prefix}-GRN-YYYY-000001 per receipt; every line that puts stock on hand gets its own {settings.document_prefix}-LOT number when posted. Cost is recorded as metadata only (no payables or valuation).</p>
      </Card>
      <Card className="p-0 overflow-x-auto">
        <div className="p-3 flex gap-2 items-center">
          <span className="text-sm">Status</span>
          <div className="w-40"><Select value={status} onChange={setStatus} options={(vocab?.receipt_statuses || []).map((s) => ({ value: s, label: s }))} placeholder="All" /></div>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>GRN</TableHead><TableHead>Supplier</TableHead><TableHead>Slip / invoice</TableHead><TableHead>Received</TableHead><TableHead>Lines</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {receipts.map((r) => (
              <TableRow key={r.id} className={`cursor-pointer ${selected === r.id ? "bg-muted" : ""}`} onClick={() => setSelected(r.id)} data-testid={`grn-row-${r.receipt_no}`}>
                <TableCell className="font-mono">{r.receipt_no}</TableCell>
                <TableCell>{r.supplier_code}</TableCell>
                <TableCell>{r.delivery_slip_no || "—"} / {r.invoice_no || "—"}</TableCell>
                <TableCell>{r.received_at?.slice(0, 10)}</TableCell>
                <TableCell>{r.lines.length}</TableCell>
                <TableCell><Badge variant={receiptStatusVariant(r.status)}>{r.status}</Badge></TableCell>
              </TableRow>
            ))}
            {receipts.length === 0 && <TableRow><TableCell colSpan={6} className="text-sm text-muted-foreground">No receipts yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
      {selected && <ReceiptDetail key={selected} receiptId={selected} items={items} locations={locations} vocab={vocab} onChanged={() => { load(); onChanged(); }} />}
    </div>
  );
}

// ================================================================ Lots & expiry

function LotRow({ lot, extra }) {
  return (
    <TableRow data-testid={`lot-row-${lot.lot_no}`}>
      <TableCell className="font-mono">{lot.lot_no}{extra}</TableCell>
      <TableCell><span className="font-mono">{lot.item_code}</span></TableCell>
      <TableCell>{lot.supplier_lot || "—"}<div className="text-xs text-muted-foreground">{lot.supplier_code}</div></TableCell>
      <TableCell className="font-mono text-xs">{lot.receipt_no}</TableCell>
      <TableCell className="text-xs">{lotDateLabel(lot)}{lot.days_to_date != null && <div>{lot.days_to_date} days</div>}</TableCell>
      <TableCell>{lot.on_hand} {lot.base_uom}<div className="text-xs text-muted-foreground">{Object.entries(lot.by_status || {}).map(([s, v]) => `${s} ${v}`).join(" · ")}</div></TableCell>
      <TableCell className="space-x-1">
        <Badge variant={lot.status === "active" ? "secondary" : "outline"}>{lot.status}</Badge>
        {lot.expired && <Badge variant="destructive">expired</Badge>}
        {lot.past_best_before && <Badge variant="outline">past best-before</Badge>}
        {lot.near_expiry && !lot.expired && <Badge variant="outline">near expiry</Badge>}
      </TableCell>
    </TableRow>
  );
}

const lotHeader = (
  <TableHeader><TableRow><TableHead>Rudras lot</TableHead><TableHead>Item</TableHead><TableHead>Supplier lot</TableHead><TableHead>Receipt</TableHead><TableHead>Date</TableHead><TableHead>On hand</TableHead><TableHead>Flags</TableHead></TableRow></TableHeader>
);

export function LotsPanel({ items, settings }) {
  const [filters, setFilters] = useState({ item_id: "", q: "" });
  const [lots, setLots] = useState([]);
  const [report, setReport] = useState(null);
  const [asOf, setAsOf] = useState(todayISO());
  const [days, setDays] = useState(String(settings.near_expiry_days || 7));
  const [orderItem, setOrderItem] = useState("");
  const [policy, setPolicy] = useState("");
  const [order, setOrder] = useState(null);

  const loadLots = useCallback(async () => {
    try { setLots((await api.get("/inventory/lots", { params: compactPayload(filters) })).data.lots || []); } catch (e) { fail(e, "Could not load lots"); }
  }, [filters]);
  const loadReport = useCallback(async () => {
    try { setReport((await api.get("/inventory/lots/expiry", { params: compactPayload({ as_of: asOf, days }) })).data); } catch (e) { fail(e, "Could not load expiry report"); }
  }, [asOf, days]);
  useEffect(() => { loadLots(); }, [loadLots]);
  useEffect(() => { loadReport(); }, [loadReport]);
  useEffect(() => {
    if (!orderItem) { setOrder(null); return; }
    api.get("/inventory/lots/allocation-order", { params: compactPayload({ item_id: orderItem, policy, as_of: asOf }) })
      .then((r) => setOrder(r.data)).catch((e) => fail(e, "Could not load allocation order"));
  }, [orderItem, policy, asOf]);

  const sections = useMemo(() => (report ? [["Expired", report.expired], ["Past best-before", report.past_best_before], [`Expiring within ${report.near_expiry_days} days`, report.near_expiry]] : []), [report]);

  return (
    <div className="space-y-4" data-testid="inventory-lots-panel">
      <Card className="p-4 space-y-3">
        <div className="flex flex-wrap gap-2 items-end">
          <Field label="As of"><Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="lots-as-of" /></Field>
          <Field label="Near-expiry window (days)"><Input value={days} inputMode="numeric" className="w-24" onChange={(e) => setDays(e.target.value)} /></Field>
        </div>
        {sections.map(([title, rows]) => (
          <div key={title}>
            <div className="text-sm font-medium">{title} ({rows.length})</div>
            {rows.length > 0 && <Table>{lotHeader}<TableBody>{rows.map((l) => <LotRow key={l.id} lot={l} />)}</TableBody></Table>}
          </div>
        ))}
      </Card>

      <Card className="p-4 space-y-3" data-testid="lots-allocation">
        <h3 className="font-semibold">Picking order (FEFO / FIFO) — query only</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Field label="Item"><Select value={orderItem} onChange={setOrderItem} options={itemOptions(items)} placeholder="Choose…" testId="lots-order-item" /></Field>
          <Field label="Policy"><Select value={policy} onChange={setPolicy} options={[{ value: "FEFO", label: "FEFO — first expiry first out" }, { value: "FIFO", label: "FIFO — first in first out" }]} placeholder="Item's policy" /></Field>
        </div>
        {order && (
          <>
            <p className="text-xs text-muted-foreground">{order.policy} ({order.policy_source}) as of {order.as_of}. Stock without a lot (opening balances): {order.unlotted_available} {order.base_uom}. {order.note}</p>
            <Table>{lotHeader}<TableBody>{order.lots.map((l) => <LotRow key={`${l.id}:${l.pick_location_id}`} lot={{ ...l, on_hand: l.pick_qty, by_status: {} }} extra={<Badge className="ml-2">#{l.pick_rank}</Badge>} />)}</TableBody></Table>
            {order.lots.length === 0 && <p className="text-sm text-muted-foreground">No available lot stock.</p>}
            {order.excluded_expired.length > 0 && <p className="text-xs text-destructive">{order.excluded_expired.length} expired lot(s) excluded from picking.</p>}
          </>
        )}
      </Card>

      <Card className="p-0 overflow-x-auto">
        <div className="p-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Field label="Item"><Select value={filters.item_id} onChange={(v) => setFilters({ ...filters, item_id: v })} options={itemOptions(items)} placeholder="All items" /></Field>
          <Field label="Search lot / supplier lot"><Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="lots-search" /></Field>
        </div>
        <Table>{lotHeader}<TableBody>
          {lots.map((l) => <LotRow key={l.id} lot={l} />)}
          {lots.length === 0 && <TableRow><TableCell colSpan={7} className="text-sm text-muted-foreground">No lots yet. Lots are created when a goods-inward receipt is posted.</TableCell></TableRow>}
        </TableBody></Table>
      </Card>
    </div>
  );
}
